<template>
  <div class="rag-analysis-page">
    <!-- Breadcrumb Navigation -->
    <div class="breadcrumb">
      <router-link to="/" class="breadcrumb-item">
        <i class="fas fa-home"></i>
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <router-link to="/projects" class="breadcrumb-item">
        Projects
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <router-link :to="`/projects/${projectId}`" class="breadcrumb-item">
        {{ project?.name || 'Project Hub' }}
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-item active">COI, Endorsement & Policy Intelligence</span>
    </div>

    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-icon">
          <i class="fas fa-search"></i>
        </div>
        <div class="header-text">
          <h1 class="page-title">COI, Endorsement & Policy Intelligence</h1>
          <p class="page-subtitle">
            Analyze compliance documents against extracted contract requirements using advanced AI technology.
          </p>
        </div>
      </div>
      <div class="header-actions">
        <button @click="viewEnhancedAnalytics" class="btn btn-primary" :disabled="loadingEnhancedAnalytics">
          <i class="fas fa-users"></i> 
          <span v-if="loadingEnhancedAnalytics">Loading...</span>
          <span v-else>Enhanced Analytics</span>
        </button>
        <router-link to="/rag-analytics-demo" class="btn btn-secondary">
          <i class="fas fa-chart-line"></i> View Demo Report
        </router-link>
        <router-link
          v-if="canAccessRagAdmin"
          :to="{ path: '/rag-admin-dashboard', query: { fromProject: projectId } }"
          class="btn btn-outline-primary"
        >
          <i class="fas fa-cogs"></i> RAG admin
        </router-link>
        <router-link :to="`/projects/${projectId}/documents`" class="btn btn-outline-primary">
          <i class="fas fa-arrow-left"></i> Back to Document Intelligence
        </router-link>
      </div>
    </div>

    <!-- Notifications -->
    <div v-if="showSuccessMessage" class="notification success-notification">
      <i class="fas fa-check-circle"></i>
      {{ showSuccessMessage }}
    </div>
    <div v-if="showErrorMessage" class="notification error-notification">
      <i class="fas fa-exclamation-circle"></i>
      {{ showErrorMessage }}
    </div>

    <!-- Contract Intelligence Status -->
    <div class="status-section">
      <div class="status-card" :class="{ 'disabled': !hasContractIntelligence }">
        <div class="status-icon">
          <i v-if="!hasContractIntelligence" class="fas fa-exclamation-triangle"></i>
          <i v-else class="fas fa-check-circle"></i>
        </div>
        <div class="status-content">
          <h3 v-if="!hasContractIntelligence">Comprehensive Check Requirements Required</h3>
          <h3 v-else>Multi-Tier Intelligence Ready</h3>
          
          <p v-if="!hasContractIntelligence">
            Configure comprehensive check requirements for this project to enable intelligent compliance analysis.
          </p>
          <p v-else>
            ✅ {{ extractedRequirements.length }} intelligence layers configured and ready for analysis.
          </p>
        </div>
        <div class="status-actions" v-if="!hasContractIntelligence">
          <router-link :to="`/projects/${projectId}/documents`" class="btn btn-primary">
            <i class="fas fa-cogs"></i> Configure Comprehensive Checks
          </router-link>
        </div>
      </div>
    </div>

    <!-- COI, Endorsement & Policy Intelligence Workspace -->
    <div v-if="hasContractIntelligence" class="rag-workspace">
      
      <!-- Document Request Section -->
      <div class="workspace-section">
        <div class="section-header">
          <h2><i class="fas fa-envelope"></i> Request Client Documents</h2>
          <p class="section-description">Send secure document requests to clients for bulk compliance document uploads</p>
        </div>
        
        <div class="request-workspace">
          <div class="request-card">
            <h4><i class="fas fa-paper-plane"></i> Send Document Request</h4>
            <p class="card-description">Request COIs, certificates, and other compliance documents from your clients</p>
            
            <div class="request-form">
              <div class="form-row">
                <div class="form-group">
                  <label>Request Type</label>
                  <select v-model="requestForm.type">
                    <option value="coi">Certificate of Insurance (COI)</option>
                    <option value="compliance">General Compliance Documents</option>
                    <option value="certificates">Professional Certificates</option>
                    <option value="custom">Custom Document Request</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Priority</label>
                  <select v-model="requestForm.priority">
                    <option value="standard">Standard (7 days)</option>
                    <option value="urgent">Urgent (3 days)</option>
                    <option value="asap">ASAP (24 hours)</option>
                  </select>
                </div>
              </div>
              
              <div class="form-group">
                <label>Message to Client</label>
                <textarea 
                  v-model="requestForm.message" 
                  placeholder="Please provide the following documents for compliance verification..."
                  rows="3"
                ></textarea>
              </div>
              
              <div class="form-actions">
                <button @click="sendDocumentRequest" class="btn btn-primary" :disabled="sending">
                  <i class="fas fa-paper-plane"></i>
                  {{ sending ? 'Sending...' : 'Send Request' }}
                </button>
              </div>
            </div>
          </div>
          
          <!-- Recent Requests -->
          <div class="request-card">
            <h4><i class="fas fa-history"></i> Recent Requests</h4>
            <p class="card-description">Track document requests and responses</p>
            
            <div class="requests-list">
              <div v-for="request in recentRequests" :key="request.id" class="request-item">
                <div class="request-info">
                  <h5>{{ request.type }}</h5>
                  <p>{{ request.message.substring(0, 80) }}...</p>
                  <span class="request-date">{{ formatDate(request.created_at) }}</span>
                </div>
                <div class="request-status" :class="request.status">
                  <i :class="getStatusIcon(request.status)"></i>
                  <span>{{ request.status.replace('_', ' ') }}</span>
                </div>
              </div>
              
              <div v-if="!recentRequests.length" class="no-requests">
                <i class="fas fa-inbox"></i>
                <p>No document requests yet</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Document Upload Section -->
      <div class="workspace-section">
        <div class="section-header">
          <h2><i class="fas fa-upload"></i> Document Upload</h2>
          <p class="section-description">Upload compliance documents for COI analysis (supports bulk uploads)</p>
        </div>
        
        <div class="upload-workspace">
          <div class="bulk-uploader">
            <div 
              class="bulk-drop-zone" 
              :class="{ 'active': isDragging, 'has-files': selectedFiles.length > 0 }"
              @drop="handleBulkDrop"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
            >
              <div v-if="selectedFiles.length === 0" class="drop-zone-content">
                <i class="fas fa-cloud-upload-alt"></i>
                <h3>Bulk Document Upload</h3>
                <p>Drop multiple files here or <label for="bulk-input" class="file-label">browse</label></p>
                <input 
                  id="bulk-input" 
                  type="file" 
                  @change="handleBulkSelect" 
                  style="display: none;"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                />
                <small>Supported: PDF, DOC, DOCX, TXT, Images • Max 50 files at once</small>
              </div>
              
              <div v-else class="files-preview">
                <h4>{{ selectedFiles.length }} files selected</h4>
                <div class="files-list">
                  <div v-for="(file, index) in selectedFiles.slice(0, 5)" :key="index" class="file-item">
                    <i :class="getFileIcon(file.name)"></i>
                    <span class="file-name">{{ file.name }}</span>
                    <span class="file-size">{{ formatFileSize(file.size) }}</span>
                    <button @click="removeFile(index)" class="remove-btn">×</button>
                  </div>
                  <div v-if="selectedFiles.length > 5" class="more-files">
                    + {{ selectedFiles.length - 5 }} more files
                  </div>
                </div>
                
                <div class="upload-actions">
                  <button @click="clearAllFiles" class="btn btn-secondary">
                    <i class="fas fa-trash"></i> Clear All
                  </button>
                  <button @click="startRAGAnalysis" class="btn btn-primary" :disabled="ragAnalyzing">
                    <i class="fas fa-brain"></i>
                    {{ ragAnalyzing ? 'Running COI Analysis...' : 'Start COI Analysis' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Analysis Results Section -->
      <div class="workspace-section">
        <div class="section-header">
          <h2><i class="fas fa-chart-pie"></i> Analysis Results</h2>
          <p class="section-description">COI analysis results and compliance insights</p>
        </div>
        
        <div class="results-workspace">
          <!-- Empty State -->
          <div v-if="analysisResults.length === 0" class="empty-results-state">
            <div class="empty-icon">
              <i class="fas fa-chart-pie"></i>
            </div>
            <h3>No Analysis Results Yet</h3>
            <p>Upload documents above to start COI analysis and view results here.</p>
          </div>

          <!-- Results Content (when data exists) -->
          <div v-else>
            <!-- Compliance Overview -->
            <div class="compliance-overview">
            <div class="compliance-metric">
              <div class="metric-circle" :style="{ '--progress': overallComplianceRate * 100 + '%' }">
                <span class="metric-value">{{ Math.round(overallComplianceRate * 100) }}%</span>
                <span class="metric-label">Overall Compliance</span>
              </div>
            </div>
            
            <div class="compliance-breakdown">
              <div class="breakdown-item passing">
                <i class="fas fa-check-circle"></i>
                <span>{{ passingChecks }} Requirements Met</span>
              </div>
              <div class="breakdown-item failing">
                <i class="fas fa-exclamation-triangle"></i>
                <span>{{ failingChecks }} Requirements Failed</span>
              </div>
              <div class="breakdown-item pending">
                <i class="fas fa-clock"></i>
                <span>{{ pendingChecks }} Pending Review</span>
              </div>
            </div>
          </div>
          
          <!-- Detailed Results -->
          <div class="results-list">
            <h4>Document Analysis Results</h4>
            <div v-for="result in analysisResults" :key="result.id" class="result-item">
              <div class="result-header">
                <div class="document-info">
                  <h5>{{ result.document_name }}</h5>
                  <span v-if="result.document_type" class="document-type-badge" :class="getDocumentTypeClass(result.document_type)">
                    {{ formatDocumentType(result.document_type) }}
                  </span>
                </div>
                <div class="result-score" :class="getScoreClass(result.compliance_score || 0)">
                  {{ Math.round((result.compliance_score || 0) * 100) }}%
                </div>
              </div>
              
              <div class="result-summary">
                <div class="summary-item">
                  <i class="fas fa-check text-success"></i>
                  <span>{{ (result.passed_requirements || []).length }} passed</span>
                </div>
                <div class="summary-item">
                  <i class="fas fa-times text-danger"></i>
                  <span>{{ (result.failed_requirements || []).length }} failed</span>
                </div>
                <div class="summary-item">
                  <i class="fas fa-question text-warning"></i>
                  <span>{{ (result.unclear_requirements || []).length }} unclear</span>
                </div>
                <div v-if="result.confidence_score" class="summary-item">
                  <i class="fas fa-brain text-info"></i>
                  <span>{{ Math.round((result.confidence_score || 0) * 100) }}% AI confidence</span>
                </div>
              </div>
              
              <!-- RAG Intelligence Insights -->
              <div v-if="result.knowledge_insights && result.knowledge_insights.length > 0" class="rag-insights">
                <h6><i class="fas fa-lightbulb"></i> Knowledge Insights</h6>
                <div class="insights-list">
                  <div v-for="insight in result.knowledge_insights.slice(0, 3)" :key="insight.id" class="insight-item">
                    <span class="insight-confidence">{{ Math.round(insight.confidence * 100) }}%</span>
                    <span class="insight-text">{{ insight.pattern.substring(0, 100) }}{{ insight.pattern.length > 100 ? '...' : '' }}</span>
                  </div>
                  <div v-if="result.knowledge_insights.length > 3" class="more-insights">
                    + {{ result.knowledge_insights.length - 3 }} more insights
                  </div>
                </div>
              </div>
              
                            <!-- Pattern Matches -->
              <div v-if="result.pattern_matches && result.pattern_matches.length > 0" class="pattern-matches">
                <h6><i class="fas fa-search"></i> Pattern Matches</h6>
                <div class="patterns-list">
                  <span v-for="pattern in result.pattern_matches.slice(0, 3)" :key="pattern.pattern_id"
                        class="pattern-badge"
                        :class="getPatternClass(pattern.confidence)">
                    {{ pattern.pattern_type }} ({{ Math.round(pattern.confidence * 100) }}%)
                  </span>
                  <span v-if="result.pattern_matches.length > 3" class="more-patterns">
                    +{{ result.pattern_matches.length - 3 }} more
                  </span>
                </div>
              </div>

              <!-- Strategic Recommendations (Gap Analysis) -->
              <div v-if="result.coverage_recommendations && result.coverage_recommendations.length > 0" class="recommendations-section">
                <h6><i class="fas fa-lightbulb"></i> Coverage Recommendations</h6>
                <div class="recommendations-list">
                  <div v-for="rec in result.coverage_recommendations.slice(0, 2)" :key="rec.category" 
                       class="recommendation-item" :class="getPriorityClass(rec.priority)">
                    <div class="rec-header">
                      <span class="rec-title">{{ rec.title }}</span>
                      <span class="rec-priority">{{ rec.priority.toUpperCase() }}</span>
                    </div>
                    <p class="rec-description">{{ truncateText(rec.analysis, 120) }}</p>
                  </div>
                  <div v-if="result.coverage_recommendations.length > 2" class="more-recommendations">
                    <small>+{{ result.coverage_recommendations.length - 2 }} more recommendations available</small>
                  </div>
                </div>
              </div>

              <!-- Enhancement Opportunities -->
              <div v-if="result.enhancement_opportunities && result.enhancement_opportunities.length > 0" class="enhancements-section">
                <h6><i class="fas fa-arrow-up"></i> Enhancement Opportunities</h6>
                <div class="enhancements-list">
                  <div v-for="enh in result.enhancement_opportunities.slice(0, 2)" :key="enh.category" 
                       class="enhancement-item">
                    <span class="enh-title">{{ enh.title }}</span>
                    <span class="enh-value">{{ enh.business_value }}</span>
                  </div>
                </div>
              </div>
              
              <div class="result-actions">
                <button @click="viewDetailedAnalysis(result)" class="btn btn-sm btn-outline-primary">
                  <i class="fas fa-eye"></i> View Details
                </button>
                <button @click="exportResult(result)" class="btn btn-sm btn-outline-secondary">
                  <i class="fas fa-download"></i> Export
                </button>
              </div>
            </div>
          </div>
          
          <!-- Export All -->
          <div class="export-section">
            <button @click="exportAllResults" class="btn btn-primary">
              <i class="fas fa-file-export"></i> Export Complete Analysis Report
            </button>
          </div>
        </div>
          </div> <!-- End v-else -->
      </div>
    </div>

    <!-- Disabled State -->
    <div v-else class="disabled-workspace">
      <div class="disabled-content">
        <i class="fas fa-lock"></i>
        <h3>Contract Intelligence Required</h3>
        <p>Complete contract analysis first to extract compliance requirements, then return here to run COI analysis on client documents.</p>
        <router-link :to="`/projects/${projectId}/documents`" class="btn btn-primary">
          <i class="fas fa-file-contract"></i> Start Contract Intelligence
        </router-link>
      </div>
    </div>
  </div>

  <!-- Detailed Analysis Modal -->
  <div v-if="showDetailedModal" class="modal-overlay" @click="closeDetailedModal">
    <div class="detailed-modal rag-split-pane-modal" @click.stop>
      <div class="modal-header">
        <h3><i class="fas fa-file-alt"></i> Detailed Analysis: {{ selectedAnalysisResult?.document_name }}</h3>
        <button @click="closeDetailedModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-content" v-if="selectedAnalysisResult">
        <!-- Split-Pane Layout -->
        <div class="split-pane-container">
          <div class="split-pane-layout">
            <!-- Left Panel: Document Viewer -->
            <div class="document-panel">
              <div class="panel-header">
                <h4><i class="fas fa-file-pdf"></i> Source Document</h4>
                <p class="panel-subtitle">{{ selectedAnalysisResult.document_name }}</p>
              </div>
              <div class="document-viewer">
                <div class="document-actions">
                  <a :href="getDocumentUrl(selectedAnalysisResult)" 
                     target="_blank" 
                     class="btn btn-outline btn-sm">
                    <i class="fas fa-external-link-alt"></i> Open Full Size
                  </a>
                  <button @click="handleDownload(selectedAnalysisResult)" 
                     :disabled="downloading"
                     class="btn btn-outline btn-sm">
                    <i :class="downloading ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i> 
                    {{ downloading ? 'Downloading...' : 'Download' }}
                  </button>
                </div>
                <!-- Document preview area -->
                <div class="document-preview">
                  <div v-if="isPDF(selectedAnalysisResult)" class="pdf-container">
                    <!-- PDF embed with same-origin support -->
                    <embed :src="getDocumentUrl(selectedAnalysisResult)" 
                           type="application/pdf"
                           class="pdf-preview"
                           title="RAG Analysis Document Preview" />
                    <div class="pdf-fallback">
                      <p><i class="fas fa-file-pdf"></i> PDF Viewer</p>
                      <p class="small text-muted">If the PDF doesn't display above, <a :href="getDocumentUrl(selectedAnalysisResult)" target="_blank">open in new tab</a></p>
                    </div>
                  </div>
                  <div v-else-if="getDocumentUrl(selectedAnalysisResult) !== '#'" class="preview-placeholder">
                    <i class="fas fa-file-alt"></i>
                    <p>Document preview not available for this file type</p>
                    <p class="small text-muted">Click "Open Full Size" to view the document</p>
                  </div>
                  <div v-else class="no-document">
                    <i class="fas fa-file-exclamation"></i>
                    <p>Document not available for preview</p>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Right Panel: Analysis Results -->
            <div class="results-panel">
              <div class="panel-header">
                <h4><i class="fas fa-chart-line"></i> Analysis Results</h4>
                <p class="panel-subtitle">Comprehensive compliance analysis</p>
              </div>
              <div class="results-content">

        
        <!-- Document Overview -->
        <div class="detail-section">
          <h4><i class="fas fa-info-circle"></i> Document Overview</h4>
          <div class="detail-grid">
            <div class="detail-item">
              <label>Document Type:</label>
              <span class="document-type-badge" :class="getDocumentTypeClass(selectedAnalysisResult.document_type)">
                {{ formatDocumentType(selectedAnalysisResult.document_type) }}
              </span>
            </div>
            <div class="detail-item">
              <label>Compliance Score:</label>
              <span class="score-badge" :class="getScoreClass(selectedAnalysisResult.compliance_score)">
                {{ Math.round((selectedAnalysisResult.compliance_score || 0) * 100) }}%
              </span>
            </div>
            <div class="detail-item">
              <label>AI Confidence:</label>
                              <span class="confidence-badge" :class="getGenericConfidenceClass(selectedAnalysisResult.confidence_score)">
                {{ Math.round((selectedAnalysisResult.confidence_score || 0) * 100) }}%
              </span>
            </div>
            <div class="detail-item">
              <label>Analysis Date:</label>
              <span>{{ new Date(selectedAnalysisResult.analysis_timestamp).toLocaleString() }}</span>
            </div>
          </div>
        </div>

        <!-- Requirements Results -->
        <div class="detail-section">
          <h4><i class="fas fa-clipboard-check"></i> Requirements Results</h4>
          <div class="requirements-summary">
            <div class="req-stat passed">
              <i class="fas fa-check-circle"></i>
              <span>{{ (selectedAnalysisResult.passed_requirements || []).length }} Passed</span>
            </div>
            <div class="req-stat failed">
              <i class="fas fa-times-circle"></i>
              <span>{{ (selectedAnalysisResult.failed_requirements || []).length }} Failed</span>
            </div>
            <div class="req-stat unclear">
              <i class="fas fa-question-circle"></i>
              <span>{{ (selectedAnalysisResult.unclear_requirements || []).length }} Unclear</span>
            </div>
          </div>
          
          <!-- Detailed Requirements Lists -->
          <div v-if="(selectedAnalysisResult.passed_requirements || []).length > 0" class="requirement-group">
            <h5 class="req-group-title passed">✅ Passed Requirements</h5>
            <div v-for="req in selectedAnalysisResult.passed_requirements" :key="req.id" class="requirement-item passed">
              <strong>{{ req.title || req.name }}</strong>
              <p v-if="req.description">{{ req.description }}</p>
              <p v-if="req.evidence" class="evidence">Evidence: {{ req.evidence }}</p>
            </div>
          </div>
          
          <div v-if="(selectedAnalysisResult.failed_requirements || []).length > 0" class="requirement-group">
            <h5 class="req-group-title failed">❌ Failed Requirements</h5>
            <div v-for="req in selectedAnalysisResult.failed_requirements" :key="req.id" class="requirement-item failed">
              <strong>{{ req.title || req.name }}</strong>
              <p v-if="req.description">{{ req.description }}</p>
              <p v-if="req.reason" class="reason">Reason: {{ req.reason }}</p>
            </div>
          </div>
          
          <div v-if="(selectedAnalysisResult.unclear_requirements || []).length > 0" class="requirement-group">
            <h5 class="req-group-title unclear">❓ Unclear Requirements</h5>
            <div v-for="req in selectedAnalysisResult.unclear_requirements" :key="req.id" class="requirement-item unclear">
              <strong>{{ req.title || req.name }}</strong>
              <p v-if="req.description">{{ req.description }}</p>
              <p v-if="req.notes" class="notes">Notes: {{ req.notes }}</p>
            </div>
          </div>
        </div>

        <!-- Knowledge Insights -->
        <div v-if="(selectedAnalysisResult.knowledge_insights || []).length > 0" class="detail-section">
          <h4><i class="fas fa-lightbulb"></i> Knowledge Insights</h4>
          <div v-for="insight in selectedAnalysisResult.knowledge_insights" :key="insight.id" class="insight-item">
            <div class="insight-header">
              <span class="insight-type">{{ insight.type || 'General' }}</span>
                              <span class="insight-confidence" :class="getGenericConfidenceClass(insight.confidence)">
                {{ Math.round((insight.confidence || 0) * 100) }}% confidence
              </span>
            </div>
            <p>{{ insight.description || insight.insight }}</p>
            <div v-if="insight.recommendations" class="insight-recommendations">
              <strong>Recommendations:</strong>
              <ul>
                <li v-for="rec in insight.recommendations" :key="rec">{{ rec }}</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Pattern Matches -->
        <div v-if="(selectedAnalysisResult.pattern_matches || []).length > 0" class="detail-section">
          <h4><i class="fas fa-search"></i> Pattern Matches</h4>
          <div v-for="pattern in selectedAnalysisResult.pattern_matches" :key="pattern.id" class="pattern-item">
            <div class="pattern-header">
              <span class="pattern-type">{{ pattern.pattern_type || 'Unknown' }}</span>
                              <span class="pattern-confidence" :class="getGenericConfidenceClass(pattern.confidence)">
                {{ Math.round((pattern.confidence || 0) * 100) }}% match
              </span>
            </div>
            <p><strong>Found:</strong> {{ pattern.matched_text || pattern.description }}</p>
            <p v-if="pattern.location"><strong>Location:</strong> {{ pattern.location }}</p>
          </div>
        </div>

        <!-- Coverage Recommendations -->
        <div v-if="(selectedAnalysisResult.coverage_recommendations || []).length > 0" class="detail-section">
          <h4><i class="fas fa-shield-alt"></i> Coverage Recommendations</h4>
          <div v-for="rec in selectedAnalysisResult.coverage_recommendations" :key="rec.id" class="recommendation-item">
            <div class="rec-header">
              <span class="rec-priority" :class="getPriorityClass(rec.priority)">{{ rec.priority || 'Medium' }}</span>
              <span class="rec-type">{{ rec.coverage_type || 'General' }}</span>
            </div>
            <h6>{{ rec.title || rec.recommendation }}</h6>
            <p v-if="rec.description">{{ rec.description }}</p>
            <p v-if="rec.rationale" class="rationale"><strong>Rationale:</strong> {{ rec.rationale }}</p>
          </div>
        </div>

        <!-- Comprehensive Analysis (Gemini Results) -->
        <div v-if="selectedAnalysisResult.comprehensive_analysis" class="detail-section">
          <h4><i class="fas fa-brain"></i> Comprehensive Analysis</h4>
          <div class="comprehensive-analysis-content">
            <div class="analysis-text" v-html="formatAnalysisText(selectedAnalysisResult.comprehensive_analysis)"></div>
          </div>
        </div>

        <!-- Gap Intelligence Results -->
        <div v-if="selectedAnalysisResult.enhanced_gap_analysis" class="detail-section">
          <h4><i class="fas fa-exclamation-triangle"></i> Gap Intelligence</h4>
          
          <!-- Risk Assessment -->
          <div v-if="selectedAnalysisResult.enhanced_gap_analysis.risk_assessment" class="risk-assessment">
            <h5>🎯 Risk Assessment</h5>
            <div class="risk-summary">
              <div class="risk-level" :class="getRiskLevelClass(selectedAnalysisResult.enhanced_gap_analysis.risk_assessment.risk_level)">
                <i class="fas fa-shield-alt"></i>
                <span>{{ selectedAnalysisResult.enhanced_gap_analysis.risk_assessment.risk_level || 'Unknown' }} Risk</span>
              </div>
              <div class="risk-score">
                <i class="fas fa-chart-line"></i>
                <span>Score: {{ selectedAnalysisResult.enhanced_gap_analysis.risk_assessment.risk_score || 0 }}/100</span>
              </div>
            </div>
            
            <!-- Risk Details -->
            <div class="risk-details">
              <div class="risk-item">
                <span class="risk-label">Critical Failures:</span>
                <span class="risk-value critical">{{ selectedAnalysisResult.enhanced_gap_analysis.risk_assessment.critical_failures || 0 }}</span>
              </div>
              <div class="risk-item">
                <span class="risk-label">High Priority Issues:</span>
                <span class="risk-value high">{{ selectedAnalysisResult.enhanced_gap_analysis.risk_assessment.high_failures || 0 }}</span>
              </div>
              <div class="risk-item">
                <span class="risk-label">Total Issues:</span>
                <span class="risk-value total">{{ selectedAnalysisResult.enhanced_gap_analysis.risk_assessment.total_failures || 0 }}</span>
              </div>
            </div>
          </div>

          <!-- Compliance Patterns -->
          <div v-if="selectedAnalysisResult.enhanced_gap_analysis.compliance_patterns && selectedAnalysisResult.enhanced_gap_analysis.compliance_patterns.length > 0" class="compliance-patterns">
            <h5>📊 Compliance Patterns</h5>
            <div v-for="pattern in selectedAnalysisResult.enhanced_gap_analysis.compliance_patterns" :key="pattern.requirement" class="pattern-item">
              <div class="pattern-header">
                <span class="pattern-type" :class="getPatternTypeClass(pattern.type)">{{ pattern.type }}</span>
                <span class="pattern-severity" :class="getSeverityClass(pattern.severity)">{{ pattern.severity }}</span>
              </div>
              <div class="pattern-content">
                <strong>{{ pattern.requirement }}</strong>
                <div v-if="pattern.pattern_indicators && pattern.pattern_indicators.length > 0" class="pattern-indicators">
                  <ul>
                    <li v-for="indicator in pattern.pattern_indicators" :key="indicator">{{ indicator }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <!-- Predictive Insights -->
          <div v-if="selectedAnalysisResult.enhanced_gap_analysis.predictive_insights" class="predictive-insights">
            <h5>🔮 Predictive Insights</h5>
            
            <div v-if="selectedAnalysisResult.enhanced_gap_analysis.predictive_insights.insights && selectedAnalysisResult.enhanced_gap_analysis.predictive_insights.insights.length > 0" class="insights-list">
              <h6>💡 Future Compliance Insights:</h6>
              <ul>
                <li v-for="insight in selectedAnalysisResult.enhanced_gap_analysis.predictive_insights.insights" :key="insight">{{ insight }}</li>
              </ul>
            </div>
            
            <div v-if="selectedAnalysisResult.enhanced_gap_analysis.predictive_insights.recommendations && selectedAnalysisResult.enhanced_gap_analysis.predictive_insights.recommendations.length > 0" class="recommendations-list">
              <h6>🎯 Recommendations:</h6>
              <ul>
                <li v-for="rec in selectedAnalysisResult.enhanced_gap_analysis.predictive_insights.recommendations" :key="rec">{{ rec }}</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Processing Metadata -->
        <div v-if="selectedAnalysisResult.processing_metadata" class="detail-section">
          <h4><i class="fas fa-cog"></i> Processing Details</h4>
          <div class="processing-info">
            <div class="info-item">
              <span class="info-label">Model Used:</span>
              <span class="info-value">{{ selectedAnalysisResult.model_used || 'Unknown' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Provider:</span>
              <span class="info-value">{{ selectedAnalysisResult.processing_metadata.provider || 'Unknown' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Native PDF Processing:</span>
              <span class="info-value" :class="selectedAnalysisResult.processing_metadata.native_pdf_processing ? 'text-success' : 'text-warning'">
                {{ selectedAnalysisResult.processing_metadata.native_pdf_processing ? 'Yes' : 'No' }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">PDF Converted to Image:</span>
              <span class="info-value" :class="selectedAnalysisResult.processing_metadata.pdf_converted_to_image ? 'text-warning' : 'text-success'">
                {{ selectedAnalysisResult.processing_metadata.pdf_converted_to_image ? 'Yes' : 'No' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Enhancement Opportunities -->
        <div v-if="(selectedAnalysisResult.enhancement_opportunities || []).length > 0" class="detail-section">
          <h4><i class="fas fa-arrow-up"></i> Enhancement Opportunities</h4>
          <div v-for="opportunity in selectedAnalysisResult.enhancement_opportunities" :key="opportunity.id" class="opportunity-item">
            <div class="opp-header">
              <span class="opp-impact" :class="getPriorityClass(opportunity.impact)">{{ opportunity.impact || 'Medium' }} Impact</span>
              <span class="opp-effort">{{ opportunity.effort || 'Medium' }} Effort</span>
            </div>
            <h6>{{ opportunity.title || opportunity.opportunity }}</h6>
            <p v-if="opportunity.description">{{ opportunity.description }}</p>
            <div v-if="opportunity.steps" class="opp-steps">
              <strong>Steps:</strong>
              <ol>
                <li v-for="step in opportunity.steps" :key="step">{{ step }}</li>
              </ol>
            </div>
          </div>
        </div>

        <!-- Customer Overlap Analysis -->
        <div v-if="selectedAnalysisResult.customer_overlap_analysis && Object.keys(selectedAnalysisResult.customer_overlap_analysis).length > 0" class="detail-section">
          <h4><i class="fas fa-users"></i> Customer Overlap Analysis</h4>
          <div class="customer-analysis-summary">
            <div class="analysis-stats">
              <div class="stat-item">
                <span class="stat-label">Entities Analyzed:</span>
                <span class="stat-value">{{ selectedAnalysisResult.customer_overlap_analysis.total_entities_analyzed || 0 }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Cross-Document Entities:</span>
                <span class="stat-value" :class="selectedAnalysisResult.customer_overlap_analysis.cross_document_entities > 0 ? 'text-warning' : 'text-success'">
                  {{ selectedAnalysisResult.customer_overlap_analysis.cross_document_entities || 0 }}
                </span>
              </div>
              <div class="stat-item">
                <span class="stat-label">High Risk Entities:</span>
                <span class="stat-value" :class="selectedAnalysisResult.customer_overlap_analysis.high_risk_entities > 0 ? 'text-danger' : 'text-success'">
                  {{ selectedAnalysisResult.customer_overlap_analysis.high_risk_entities || 0 }}
                </span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Legacy Matched:</span>
                <span class="stat-value text-info">{{ selectedAnalysisResult.customer_overlap_analysis.legacy_matched_entities || 0 }}</span>
              </div>
            </div>
            
            <!-- Overall Document Risk -->
            <div v-if="selectedAnalysisResult.customer_overlap_analysis.overall_document_risk" class="document-risk-assessment">
              <h6>Document Risk Assessment</h6>
              <div class="risk-level-badge" :class="getRiskLevelClass(selectedAnalysisResult.customer_overlap_analysis.overall_document_risk.risk_level)">
                {{ selectedAnalysisResult.customer_overlap_analysis.overall_document_risk.risk_level || 'UNKNOWN' }} RISK
              </div>
              <div v-if="selectedAnalysisResult.customer_overlap_analysis.overall_document_risk.risk_factors && selectedAnalysisResult.customer_overlap_analysis.overall_document_risk.risk_factors.length > 0" class="risk-factors">
                <strong>Risk Factors:</strong>
                <ul>
                  <li v-for="factor in selectedAnalysisResult.customer_overlap_analysis.overall_document_risk.risk_factors" :key="factor">
                    {{ factor }}
                  </li>
                </ul>
              </div>
            </div>

            <!-- Customer Clusters -->
            <div v-if="selectedAnalysisResult.customer_overlap_analysis.customer_clusters_involved && selectedAnalysisResult.customer_overlap_analysis.customer_clusters_involved.length > 0" class="customer-clusters">
              <h6>Customer Details</h6>
              <div v-for="cluster in selectedAnalysisResult.customer_overlap_analysis.customer_clusters_involved" :key="cluster.cluster_id" class="customer-cluster-item">
                <div class="cluster-header">
                  <span class="entity-name">{{ cluster.entity_name }}</span>
                  <span class="entity-role badge">{{ cluster.entity_role }}</span>
                  <span v-if="cluster.is_cross_document" class="cross-doc-badge badge badge-warning">Cross-Document</span>
                  <span v-if="cluster.has_legacy_history" class="legacy-badge badge badge-info">Has History</span>
                </div>
                <div class="cluster-details">
                  <div class="risk-info">
                    <span class="risk-label">Risk Level:</span>
                    <span class="risk-badge" :class="getRiskLevelClass(cluster.risk_assessment.risk_level)">
                      {{ cluster.risk_assessment.risk_level }}
                    </span>
                  </div>
                  <div v-if="cluster.legacy_compliance_rate !== undefined" class="compliance-rate">
                    <span class="compliance-label">Historical Compliance:</span>
                    <span class="compliance-value" :class="getComplianceClass(cluster.legacy_compliance_rate)">
                      {{ cluster.legacy_compliance_rate.toFixed(1) }}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Risk Prioritization -->
        <div v-if="selectedAnalysisResult.risk_prioritization && Object.keys(selectedAnalysisResult.risk_prioritization).length > 0" class="detail-section">
          <h4><i class="fas fa-exclamation-triangle"></i> Risk-Based Prioritization</h4>
          <div class="risk-prioritization-content">
            <div class="priority-summary">
              <div class="priority-level">
                <span class="priority-label">Customer Risk Level:</span>
                <span class="priority-badge" :class="getRiskLevelClass(selectedAnalysisResult.risk_prioritization.customer_risk_level)">
                  {{ selectedAnalysisResult.risk_prioritization.customer_risk_level || 'UNKNOWN' }}
                </span>
              </div>
              <div v-if="selectedAnalysisResult.risk_prioritization.priority_adjustments_applied" class="adjustments-applied">
                <i class="fas fa-check-circle text-success"></i>
                <span>Priority adjustments applied based on customer risk</span>
              </div>
            </div>
            
            <div v-if="selectedAnalysisResult.risk_prioritization.recommendation" class="risk-recommendation">
              <h6>Recommendations:</h6>
              <div class="recommendation-text">
                {{ selectedAnalysisResult.risk_prioritization.recommendation }}
              </div>
            </div>
          </div>
        </div>

        <!-- 🤖 Intelligent Compliance Agent Evaluation -->
        <div v-if="selectedAnalysisResult.agent_evaluation && selectedAnalysisResult.agent_evaluation.action && selectedAnalysisResult.agent_evaluation.action !== 'ERROR'" class="detail-section">
          <h4><i class="fas fa-robot"></i> Intelligent Compliance Agent</h4>
          <div class="agent-evaluation-content">
            <!-- Agent Recommendation Header -->
            <div class="agent-recommendation-header">
              <div class="recommendation-action">
                <span class="action-label">Recommended Action:</span>
                <span class="action-value">{{ formatActionName(selectedAnalysisResult.agent_evaluation.action) }}</span>
              </div>
              <div class="urgency-badge" :class="getUrgencyClass(selectedAnalysisResult.agent_evaluation.urgency)">
                <i class="fas fa-exclamation-circle"></i>
                {{ selectedAnalysisResult.agent_evaluation.urgency }} PRIORITY
              </div>
            </div>

            <!-- Agent Reasoning -->
            <div class="agent-reasoning">
              <h6><i class="fas fa-lightbulb"></i> AI Reasoning</h6>
              <div class="reasoning-text">
                {{ selectedAnalysisResult.agent_evaluation.reasoning }}
              </div>
            </div>

            <!-- Confidence Score -->
            <div class="confidence-metrics">
              <div class="confidence-score">
                <span class="confidence-label">Agent Confidence:</span>
                <div class="confidence-bar-container">
                  <div class="confidence-bar">
                    <div class="confidence-fill" :style="{width: (selectedAnalysisResult.agent_evaluation.overall_confidence * 100) + '%'}" :class="getConfidenceClass(selectedAnalysisResult.agent_evaluation.overall_confidence)"></div>
                  </div>
                  <span class="confidence-percentage">{{ Math.round(selectedAnalysisResult.agent_evaluation.overall_confidence * 100) }}%</span>
                </div>
              </div>
              
              <div v-if="selectedAnalysisResult.agent_evaluation.estimated_resolution_days" class="resolution-timeline">
                <span class="timeline-label">Estimated Resolution:</span>
                <span class="timeline-value">{{ selectedAnalysisResult.agent_evaluation.estimated_resolution_days }} days</span>
              </div>
            </div>

            <!-- Specific Actions -->
            <div v-if="selectedAnalysisResult.agent_evaluation.specific_actions && selectedAnalysisResult.agent_evaluation.specific_actions.length > 0" class="specific-actions">
              <h6><i class="fas fa-tasks"></i> Recommended Actions</h6>
              <ul class="action-list">
                <li v-for="(action, index) in selectedAnalysisResult.agent_evaluation.specific_actions" :key="index" class="action-item">
                  <i class="fas fa-arrow-right"></i>
                  {{ formatActionDescription(action) }}
                </li>
              </ul>
            </div>

            <!-- Risk Factors -->
            <div v-if="selectedAnalysisResult.agent_evaluation.risk_factors && selectedAnalysisResult.agent_evaluation.risk_factors.length > 0" class="agent-risk-factors">
              <h6><i class="fas fa-exclamation-triangle"></i> Risk Factors Identified</h6>
              <div class="risk-factors-list">
                <div v-for="(factor, index) in selectedAnalysisResult.agent_evaluation.risk_factors" :key="index" class="risk-factor-item">
                  <i class="fas fa-warning text-warning"></i>
                  {{ factor }}
                </div>
              </div>
            </div>

            <!-- Agent Metadata -->
            <div v-if="selectedAnalysisResult.agent_evaluation.evaluation_timestamp" class="agent-metadata">
              <div class="metadata-item">
                <i class="fas fa-clock"></i>
                <span class="metadata-label">Evaluated:</span>
                <span class="metadata-value">{{ formatDateTime(selectedAnalysisResult.agent_evaluation.evaluation_timestamp) }}</span>
              </div>
              <div v-if="selectedAnalysisResult.agent_evaluation.agent_version" class="metadata-item">
                <i class="fas fa-code-branch"></i>
                <span class="metadata-label">Agent Version:</span>
                <span class="metadata-value">{{ selectedAnalysisResult.agent_evaluation.agent_version }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Agent Error Display -->
        <div v-if="selectedAnalysisResult.agent_evaluation && selectedAnalysisResult.agent_evaluation.action === 'ERROR'" class="detail-section agent-error">
          <h4><i class="fas fa-robot"></i> Intelligent Compliance Agent</h4>
          <div class="alert alert-warning">
            <i class="fas fa-exclamation-triangle"></i>
            <strong>Agent Evaluation Error:</strong> {{ selectedAnalysisResult.agent_evaluation.reasoning }}
          </div>
        </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import auth from '@/services/auth';
import { sanitizeHtml } from '@/utils/sanitize';

export default {
  name: 'RAGAnalysis',
  props: {
    projectId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      project: null,
      loading: false,
      downloading: false,
      error: null,
      
      // Contract Intelligence Status
      hasContractIntelligence: false,
      extractedRequirements: [],
      
      // Document Request
      requestForm: {
        type: 'coi',
        priority: 'standard',
        message: 'Please provide the following documents for compliance verification as part of our ongoing insurance requirements review.'
      },
      sending: false,
      recentRequests: [],
      
      // File Upload
      selectedFiles: [],
      isDragging: false,
      ragAnalyzing: false,
      
      // Analysis Results
      analysisResults: [],
      overallComplianceRate: 0.73,
      passingChecks: 18,
      failingChecks: 7,
      pendingChecks: 3,
      
      // Notifications
      showSuccessMessage: '',
      showErrorMessage: '',
      
      // Detailed Analysis Modal
      showDetailedModal: false,
      selectedAnalysisResult: null,
      documentCache: {}, // Cache full document objects with file_url
      
      // Enhanced Analytics
      loadingEnhancedAnalytics: false
    }
  },
  async mounted() {
    await this.loadProjectData();
    await this.checkContractIntelligenceStatus();
    await this.loadRecentRequests();
    await this.loadExistingAnalysisResults();
  },
  computed: {
    canAccessRagAdmin() {
      return auth.isAdmin();
    },
  },
  methods: {
    async loadProjectData() {
      try {
        this.loading = true;
        const response = await api.get(`/projects/${this.projectId}/`);
        this.project = response.data;
      } catch (error) {
        console.error('Error loading project:', error);
        this.error = 'Failed to load project information';
      } finally {
        this.loading = false;
      }
    },
    
    async checkContractIntelligenceStatus() {
      // Check if project has comprehensive check requirements configured
      try {
        // First check if project has a default comprehensive check
        const projectResponse = await api.get(`/projects/${this.projectId}/`);
        let hasComprehensiveCheck = false;
        
        if (projectResponse.data.default_comprehensive_check) {
          hasComprehensiveCheck = true;
          this.extractedRequirements = [
            { id: 1, title: 'Client-Specific Requirements (Jersey Mike\'s template)' },
            { id: 2, title: 'Dynamic Document Intelligence' },
            { id: 3, title: 'Coverage Gap Analysis' },
            { id: 4, title: 'Strategic Recommendations' }
          ];
        } else {
          // Fallback: Check for any comprehensive check definitions for this project type
          try {
            const projectType = projectResponse.data.project_type || 'default';
            const checkDefsResponse = await api.get(`/comprehensive-check-definitions/?project_type=${projectType}`);
            hasComprehensiveCheck = checkDefsResponse.data.length > 0;
            
            if (hasComprehensiveCheck) {
              this.extractedRequirements = [
                { id: 1, title: 'Universal Compliance Requirements' },
                { id: 2, title: 'Dynamic Document Intelligence' },
                { id: 3, title: 'AI-Generated Requirements' },
                { id: 4, title: 'Coverage Analysis' }
              ];
            }
          } catch (checkError) {
            console.log('No comprehensive check definitions API available');
            // Ultimate fallback: always allow RAG analysis
            hasComprehensiveCheck = true;
            this.extractedRequirements = [
              { id: 1, title: 'Dynamic Document Intelligence' },
              { id: 2, title: 'AI-Generated Requirements' },
              { id: 3, title: 'Pattern Recognition' },
              { id: 4, title: 'Compliance Analysis' }
            ];
          }
        }
        
        this.hasContractIntelligence = hasComprehensiveCheck;
        
      } catch (error) {
        console.error('Error checking comprehensive check requirements:', error);
        // Fallback: Always allow RAG analysis (we have fallback logic in backend)
        this.hasContractIntelligence = true;
        this.extractedRequirements = [
          { id: 1, title: 'Dynamic Document Intelligence' },
          { id: 2, title: 'AI-Generated Requirements' },
          { id: 3, title: 'Fallback Analysis' }
        ];
      }
    },
    
    async loadRecentRequests() {
      // Mock recent requests - in real app, load from API
      this.recentRequests = [
        {
          id: 1,
          type: 'Certificate of Insurance',
          message: 'Please provide current COI with updated coverage limits...',
          status: 'pending',
          created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
        },
        {
          id: 2,
          type: 'Compliance Documents',
          message: 'We need your latest safety certificates and training records...',
          status: 'completed',
          created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)
        }
      ];
    },

    async loadExistingAnalysisResults() {
      try {
        console.log('🔍 Loading existing analysis results...');
        // Try to load existing RAG analysis results for this project
        const response = await api.get(`/projects/${this.projectId}/rag/results/`);
        
        if (response.data && response.data.length > 0) {
          this.analysisResults = response.data;
          console.log(`✅ Loaded ${response.data.length} existing analysis results`);
          
          // Update metrics from the most recent analysis
          if (response.data.length > 0) {
            const latestAnalysis = response.data[0];
            this.overallComplianceRate = latestAnalysis.overall_compliance_rate || 0;
            this.passingChecks = latestAnalysis.total_passed_checks || 0;
            this.failingChecks = latestAnalysis.total_failed_checks || 0;
            this.pendingChecks = latestAnalysis.total_unclear_checks || 0;
          }
        } else {
          console.log('ℹ️ No existing analysis results found');
        }
      } catch (error) {
        console.log('ℹ️ No existing analysis results or API not available:', error.message);
        // This is expected for new projects or if API endpoint doesn't exist yet
      }
    },
    
    async sendDocumentRequest() {
      try {
        this.sending = true;
        
        // Mock sending request
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        const newRequest = {
          id: Date.now(),
          type: this.requestForm.type.replace('_', ' ').toUpperCase(),
          message: this.requestForm.message,
          status: 'pending',
          created_at: new Date()
        };
        
        this.recentRequests.unshift(newRequest);
        
        // Reset form
        this.requestForm.message = 'Please provide the following documents for compliance verification as part of our ongoing insurance requirements review.';
        
        this.showSuccessMessage = 'Document request sent successfully!';
        setTimeout(() => this.showSuccessMessage = '', 5000);
        
      } catch (error) {
        console.error('Error sending request:', error);
        this.showErrorMessage = 'Failed to send document request';
        setTimeout(() => this.showErrorMessage = '', 8000);
      } finally {
        this.sending = false;
      }
    },
    
    handleBulkDrop(e) {
      e.preventDefault();
      this.isDragging = false;
      const files = Array.from(e.dataTransfer.files);
      this.addFiles(files);
    },
    
    handleBulkSelect(e) {
      const files = Array.from(e.target.files);
      this.addFiles(files);
    },
    
    addFiles(files) {
      // Limit to 50 files max
      const remainingSlots = 50 - this.selectedFiles.length;
      const filesToAdd = files.slice(0, remainingSlots);
      
      this.selectedFiles.push(...filesToAdd);
      
      if (files.length > remainingSlots) {
        this.showErrorMessage = `Only ${remainingSlots} files added. Maximum 50 files allowed.`;
        setTimeout(() => this.showErrorMessage = '', 8000);
      }
    },
    
    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },
    
    clearAllFiles() {
      this.selectedFiles = [];
    },
    
    async startRAGAnalysis() {
      if (this.selectedFiles.length === 0) return;
      
      try {
        this.ragAnalyzing = true;
        
        // Create FormData for file upload
        const formData = new FormData();
        this.selectedFiles.forEach(file => {
          formData.append('files', file);
        });
        
        // Call the real RAG analysis API
        const response = await api.post(`/projects/${this.projectId}/rag/analyze/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        
        if (response && response.data && response.data.success) {
          // Save analysis results
          this.analysisResults = response.data.analysis_results || [];
          this.selectedFiles = [];
          
          console.log('🎉 RESULTS SAVED! analysisResults.length:', this.analysisResults.length);
          console.log('🔍 Results should now display in UI...');
          
          // Show simple success notification
          this.showSuccessMessage = 'COI analysis completed successfully!';
          setTimeout(() => this.showSuccessMessage = '', 5000);
        } else {
          console.log('🔍 RESPONSE NOT SUCCESSFUL');
          const errorMsg = (response && response.data && response.data.error) || 'Analysis failed - invalid response format';
          throw new Error(errorMsg);
        }
        
      } catch (error) {
        console.error('COI analysis error:', error);
        
        // Robust error message handling - COMPLETELY SAFE
        let errorMessage = 'Failed to complete COI analysis';
        
        if (error && error.response && error.response.data) {
          if (error.response.data && error.response.data.error) {
            errorMessage = error.response.data.error;
          } else if (error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data && typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          }
        } else if (error && error.message) {
          errorMessage = error.message;
        } else if (error && typeof error === 'string') {
          errorMessage = error;
        } else {
          errorMessage = 'Unknown error occurred';
        }
        
        // Show simple error notification
        this.showErrorMessage = errorMessage;
        setTimeout(() => this.showErrorMessage = '', 8000);
      } finally {
        this.ragAnalyzing = false;
      }
    },
    
    generateMockRequirements(count) {
      const requirements = [
        'Primary and Noncontributory Coverage',
        'Additional Insured Status',
        'Minimum Coverage Limits',
        'Certificate Requirements',
        'Waiver of Subrogation',
        'Claims-Made vs Occurrence',
        'Deductible Limits',
        'Policy Renewal Dates'
      ];
      
      return requirements.slice(0, count);
    },
    
    getFileIcon(fileName) {
      const ext = fileName.split('.').pop().toLowerCase();
      switch (ext) {
        case 'pdf': return 'fas fa-file-pdf';
        case 'doc':
        case 'docx': return 'fas fa-file-word';
        case 'xls':
        case 'xlsx': return 'fas fa-file-excel';
        case 'png':
        case 'jpg':
        case 'jpeg': return 'fas fa-file-image';
        default: return 'fas fa-file';
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },
    
    getStatusIcon(status) {
      switch (status) {
        case 'pending': return 'fas fa-clock';
        case 'completed': return 'fas fa-check-circle';
        case 'failed': return 'fas fa-times-circle';
        default: return 'fas fa-question-circle';
      }
    },
    
    getScoreClass(score) {
      if (score >= 0.8) return 'excellent';
      if (score >= 0.6) return 'good';
      if (score >= 0.4) return 'warning';
      return 'poor';
    },
    
    getPatternClass(confidence) {
      if (confidence >= 0.8) return 'high-confidence';
      if (confidence >= 0.6) return 'medium-confidence';
      return 'low-confidence';
    },
    
    formatDocumentType(type) {
      const typeMap = {
        'certificate_of_insurance': 'Certificate of Insurance',
        'insurance_policy': 'Insurance Policy',
        'endorsement': 'Endorsement',
        'amendment': 'Amendment',
        'unknown': 'Document'
      };
      return typeMap[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    
    getDocumentTypeClass(type) {
      const classMap = {
        'certificate_of_insurance': 'doc-type-coi',
        'insurance_policy': 'doc-type-policy',
        'endorsement': 'doc-type-endorsement',
        'amendment': 'doc-type-amendment'
      };
      return classMap[type] || 'doc-type-unknown';
    },
    
    getPriorityClass(priority) {
      if (!priority) return 'priority-medium';
      const normalizedPriority = priority.toString().toLowerCase();
      const classMap = {
        'high': 'priority-high',
        'medium': 'priority-medium', 
        'low': 'priority-low',
        'critical': 'priority-high',
        'urgent': 'priority-high'
      };
      return classMap[normalizedPriority] || 'priority-medium';
    },
    
    getGenericConfidenceClass(confidence) {
      if (!confidence && confidence !== 0) return 'low-confidence';
      const score = parseFloat(confidence);
      if (score >= 0.8) return 'high-confidence';
      if (score >= 0.6) return 'medium-confidence';
      return 'low-confidence';
    },
    
    
    getComplianceClass(rate) {
      if (rate === undefined || rate === null) return 'compliance-unknown';
      const score = parseFloat(rate);
      if (score >= 90) return 'compliance-excellent';
      if (score >= 80) return 'compliance-good';
      if (score >= 70) return 'compliance-fair';
      if (score >= 50) return 'compliance-poor';
      return 'compliance-critical';
    },

    // 🤖 Agent Evaluation Formatting Methods
    formatActionName(action) {
      const actionMap = {
        'RESOLVE_ENTITY_MISMATCH': 'Resolve Entity Mismatch',
        'URGENT_POLICY_RENEWAL': 'Urgent Policy Renewal',
        'REQUEST_NEW_CARRIER': 'Request New Carrier',
        'STANDARD_RENEWAL_REQUEST': 'Standard Renewal Request',
        'CARRIER_PERFORMANCE_REVIEW': 'Carrier Performance Review',
        'CONTINUE_MONITORING': 'Continue Monitoring',
        'ERROR': 'System Error'
      };
      return actionMap[action] || action.replace(/_/g, ' ').toLowerCase()
        .replace(/\b\w/g, l => l.toUpperCase());
    },

    formatActionDescription(action) {
      const actionMap = {
        'request_corrected_certificate': 'Request corrected certificate from subject',
        'verify_entity_relationships': 'Verify entity relationships and franchise connections',
        'flag_for_manual_review': 'Flag for manual review by risk manager',
        'immediate_renewal_request': 'Send immediate renewal request',
        'escalate_to_risk_manager': 'Escalate to risk manager',
        'notify_project_stakeholders': 'Notify project stakeholders',
        'request_temp_coverage_if_needed': 'Request temporary coverage if needed',
        'notify_subject_carrier_change_needed': 'Notify subject that carrier change is needed',
        'provide_preferred_carrier_list': 'Provide preferred carrier recommendations',
        'schedule_followup_in_3_days': 'Schedule follow-up in 3 days',
        'send_renewal_reminder': 'Send renewal reminder',
        'schedule_followup_in_7_days': 'Schedule follow-up in 7 days',
        'track_response_time': 'Track response time',
        'review_carrier_alternatives': 'Review carrier alternatives',
        'document_performance_issues': 'Document performance issues',
        'prepare_carrier_change_recommendation': 'Prepare carrier change recommendation',
        'continue_regular_monitoring': 'Continue regular monitoring',
        'schedule_next_review': 'Schedule next review',
        'maintain_current_status': 'Maintain current status',
        'review_error': 'Review system error',
        'manual_intervention_required': 'Manual intervention required'
      };
      return actionMap[action] || action.replace(/_/g, ' ').toLowerCase()
        .replace(/\b\w/g, l => l.toUpperCase());
    },

    getUrgencyClass(urgency) {
      const urgencyMap = {
        'CRITICAL': 'urgency-critical',
        'HIGH': 'urgency-high', 
        'MEDIUM': 'urgency-medium',
        'LOW': 'urgency-low'
      };
      return urgencyMap[urgency] || 'urgency-unknown';
    },

    getConfidenceClass(confidence) {
      if (!confidence) return 'confidence-unknown';
      const score = parseFloat(confidence);
      if (score >= 0.9) return 'confidence-very-high';
      if (score >= 0.7) return 'confidence-high';
      if (score >= 0.5) return 'confidence-medium';
      return 'confidence-low';
    },

    formatDateTime(timestamp) {
      if (!timestamp) return 'N/A';
      try {
        const date = new Date(timestamp);
        return date.toLocaleString();
      } catch (e) {
        return timestamp;
      }
    },
    
    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },
    
    async viewDetailedAnalysis(result) {
      this.selectedAnalysisResult = result;
      this.showDetailedModal = true;
      
      // Pre-fetch document details for proper file_url if we don't have it cached
      if (result.document_id && !this.documentCache[result.document_id]) {
        try {
          console.log('📡 Pre-fetching document details for ID:', result.document_id);
          const response = await this.$api.get(`/documents/${result.document_id}/`);
          if (response.data) {
            this.documentCache[result.document_id] = response.data;
            console.log('✅ Cached document object with file_url for future use');
          }
        } catch (error) {
          console.warn('❌ Failed to pre-fetch document object:', error);
        }
      }
    },
    
    closeDetailedModal() {
      this.showDetailedModal = false;
      this.selectedAnalysisResult = null;
    },
    
    // Helper method to construct document URLs for PDF viewing
    getDocumentUrl(result) {
      if (!result) return '#';
      
      // Check if we have cached the full document with file_url
      if (result.document_id && this.documentCache && this.documentCache[result.document_id]) {
        const cachedDoc = this.documentCache[result.document_id];
        // Use unified helper with cached document
        const { getDocumentUrl } = require('@/utils/documentUrlHelper');
        return getDocumentUrl(cachedDoc);
      }
      
      // Use unified helper with result object directly
      const { getDocumentUrl } = require('@/utils/documentUrlHelper');
      const url = getDocumentUrl(result);
      
      // If we got a valid URL, return it
      if (url !== '#') {
        return url;
      }
      
      // FALLBACK: Construct from document_name for legacy compatibility
      if (result.document_name && result.document_name.includes('.')) {
        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000';
        
        // Handle both relative and absolute API URLs
        let urlBase;
        if (apiUrl.startsWith('http')) {
          // Full URL like 'https://app.praetorianai.io/api'
          urlBase = apiUrl.replace(/\/api\/?$/, '');
        } else if (apiUrl.startsWith('/api')) {
          // Relative URL like '/api' - use current domain
          urlBase = window.location.origin;
        } else {
          // Fallback
          urlBase = apiUrl;
        }
        
        // Handle case where document_name might contain path like "documents/filename.pdf"
        let docPath = result.document_name;
        
        // Remove any leading 'media/' or 'documents/' from the path
        docPath = docPath.replace(/^(media|documents)\//, '');
        
        // If document_name is just filename, try to construct path with documents/
        if (!docPath.includes('/') && result.document_id) {
          docPath = `documents/${docPath}`;
        }
        
        const baseUrl = `${urlBase}/media/${docPath}`;
        console.log('📄 Constructed media URL (fallback):', baseUrl);
        return baseUrl;
      } else if (result.document_id) {
        // Fallback: Try document_id with API endpoint (but this might have X-Frame-Options)
        const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000';
        
        // Handle both relative and absolute API URLs
        let urlBase;
        if (apiUrl.startsWith('http')) {
          urlBase = apiUrl.replace(/\/api\/?$/, '');
        } else if (apiUrl.startsWith('/api')) {
          urlBase = window.location.origin;
        } else {
          urlBase = apiUrl;
        }
        
        const baseUrl = `${urlBase}/api/documents/${result.document_id}/file/`;
        console.log('⚠️ Using API URL (might have X-Frame-Options):', baseUrl);
        
        // For PDF files, add parameters to hide navigation panel and show clean view
        if (this.isPDF(result)) {
          // Add PDF viewer parameters to hide navigation panel and provide clean scrolling view
          return baseUrl + '#toolbar=1&navpanes=0&scrollbar=1&view=FitH';
        }
        
        return baseUrl;
      } else {
        console.log('❌ Could not construct document URL');
        return '#';
      }
    },
    
    // Helper method to detect PDF files
    isPDF(result) {
      if (!result || !result.document_name) return false;
      return result.document_name.toLowerCase().endsWith('.pdf');
    },
    
    async handleDownload(result) {
      // Download document with proper authentication
      if (!result || !result.document_id) {
        console.warn('Cannot download: no document_id');
        return;
      }
      
      this.downloading = true;
      try {
        // Construct a doc-like object for the download helper
        const doc = {
          id: result.document_id,
          name: result.document_name || 'document',
          file_url: result.document_url || result.file_url
        };
        
        const { downloadDocument } = require('@/utils/documentUrlHelper');
        await downloadDocument(doc, api);
      } catch (error) {
        console.error('Download failed:', error);
        alert('Failed to download document. Please try again.');
      } finally {
        this.downloading = false;
      }
    },
    
    exportResult(result) {
      this.showSuccessMessage = `Exporting analysis for ${result.document_name}`;
      setTimeout(() => this.showSuccessMessage = '', 5000);
    },
    
    exportAllResults() {
      this.showSuccessMessage = 'Exporting complete analysis report...';
      setTimeout(() => this.showSuccessMessage = '', 5000);
    },
    
    async viewEnhancedAnalytics() {
      try {
        this.loadingEnhancedAnalytics = true;
        
        // Call the enhanced analytics endpoint
        const response = await api.get(`/projects/${this.projectId}/analytics/enhanced/`);
        
        if (response.data.success) {
          // Create a modal or navigate to show the results
          // For now, let's show an alert with key insights
          const report = response.data.report;
          const summary = report.executive_summary;
          
          let message = `Enhanced Analytics Report:\n\n`;
          message += `Total Customers: ${summary.total_customers}\n`;
          message += `Cross-Document Customers: ${summary.cross_document_customers}\n`;
          message += `High Risk Customers: ${summary.high_risk_customers}\n`;
          message += `Legacy Matched: ${summary.legacy_matched_customers}\n`;
          message += `Current Compliance: ${summary.compliance_rate_current}%\n`;
          message += `Historical Compliance: ${summary.legacy_compliance_rate}%\n\n`;
          
          if (report.recommendations && report.recommendations.length > 0) {
            message += `Recommendations:\n`;
            report.recommendations.forEach(rec => {
              message += `• ${rec}\n`;
            });
          }
          
          alert(message);
          
          // TODO: Create a proper modal or route to display this data
          console.log('Enhanced Analytics Report:', report);
          
        } else {
          this.showErrorMessage = `Failed to load enhanced analytics: ${response.data.error}`;
          setTimeout(() => this.showErrorMessage = '', 5000);
        }
        
      } catch (error) {
        console.error('Enhanced analytics error:', error);
        this.showErrorMessage = `Enhanced analytics error: ${error.message}`;
        setTimeout(() => this.showErrorMessage = '', 5000);
      } finally {
        this.loadingEnhancedAnalytics = false;
      }
    },

    // New methods for enhanced analysis display
    formatAnalysisText(text) {
      if (!text) return '';
      
      // Convert markdown-like formatting to HTML
      const html = text
        .replace(/### (.*)/g, '<h3>$1</h3>')
        .replace(/## (.*)/g, '<h2>$1</h2>')
        .replace(/# (.*)/g, '<h1>$1</h1>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n\n/g, '</p><p>')
        .replace(/\n/g, '<br>')
        .replace(/^/, '<p>')
        .replace(/$/, '</p>');
      // Sanitize to prevent XSS
      return sanitizeHtml(html);
    },

    getRiskLevelClass(riskLevel) {
      const level = (riskLevel || '').toLowerCase();
      return {
        'risk-critical': level === 'critical',
        'risk-high': level === 'high', 
        'risk-medium': level === 'medium',
        'risk-low': level === 'low'
      };
    },

    getPatternTypeClass(patternType) {
      const type = (patternType || '').toLowerCase();
      return {
        'pattern-failure': type.includes('failure'),
        'pattern-success': type.includes('success'),
        'pattern-warning': type.includes('warning')
      };
    },

    getSeverityClass(severity) {
      const sev = (severity || '').toLowerCase();
      return {
        'severity-critical': sev === 'critical',
        'severity-high': sev === 'high',
        'severity-medium': sev === 'medium', 
        'severity-low': sev === 'low'
      };
    }
  }
};
</script>

<style scoped>
/* RAG Analysis Page Styles */
.rag-analysis-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  min-height: 100vh;
}

/* Notifications */
.notification {
  margin: 1rem 0;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 500;
  animation: slideIn 0.3s ease-out;
}

.success-notification {
  background: #d4f4dd;
  color: #22543d;
  border: 1px solid #9ae6b4;
}

.error-notification {
  background: #fed7d7;
  color: #742a2a;
  border: 1px solid #feb2b2;
}

@keyframes slideIn {
  from { transform: translateY(-10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

/* Empty Results State */
.empty-results-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #718096;
}

.empty-icon {
  font-size: 4rem;
  color: #e2e8f0;
  margin-bottom: 1.5rem;
}

.empty-results-state h3 {
  color: #4a5568;
  margin-bottom: 0.75rem;
  font-size: 1.5rem;
}

.empty-results-state p {
  color: #718096;
  font-size: 1rem;
  max-width: 400px;
  margin: 0 auto;
}

/* Breadcrumb */
.breadcrumb {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  font-size: 0.9rem;
}

.breadcrumb-item {
  color: #667eea;
  text-decoration: none;
  transition: color 0.2s ease;
}

.breadcrumb-item:hover {
  color: #5a67d8;
  text-decoration: none;
}

.breadcrumb-item.active {
  color: #718096;
  font-weight: 500;
}

.breadcrumb-separator {
  margin: 0 0.75rem;
  color: #cbd5e0;
}

/* Page Header */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2.5rem;
  border-radius: 20px;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex: 1;
}

.header-icon {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  backdrop-filter: blur(10px);
}

.page-title {
  font-size: 2.2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: white;
}

.page-subtitle {
  margin: 0;
  opacity: 0.9;
  font-size: 1.1rem;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.header-actions .btn {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  color: white;
  border-radius: 12px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  transition: all 0.3s ease;
  text-decoration: none;
}

.header-actions .btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
  color: white;
  text-decoration: none;
}

/* Status Section */
.status-section {
  margin-bottom: 2rem;
}

.status-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2rem;
  display: flex;
  align-items: center;
  gap: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.status-card.disabled {
  opacity: 0.7;
  background: rgba(243, 244, 246, 0.95);
}

.status-icon {
  width: 60px;
  height: 60px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
}

.status-card:not(.disabled) .status-icon {
  background: linear-gradient(135deg, #10b981 0%, #065f46 100%);
}

.status-card.disabled .status-icon {
  background: linear-gradient(135deg, #f59e0b 0%, #92400e 100%);
}

.status-content {
  flex: 1;
}

.status-content h3 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
}

.status-content p {
  color: #718096;
  margin: 0;
  line-height: 1.5;
}

/* Workspace Sections */
.workspace-section {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 2.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.section-header {
  margin-bottom: 2rem;
  text-align: center;
}

.section-header h2 {
  font-size: 1.8rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 1rem 0;
}

.section-description {
  font-size: 1rem;
  color: #718096;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;
}

/* Request Workspace */
.request-workspace {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.request-card {
  background: rgba(255, 255, 255, 0.9);
  border-radius: 15px;
  padding: 2rem;
  border: 1px solid #e2e8f0;
}

.request-card h4 {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.card-description {
  color: #718096;
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
}

/* Form Styles */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.form-group select,
.form-group textarea {
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: border-color 0.2s ease;
}

.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

/* Request List */
.requests-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.request-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 10px;
  border-left: 4px solid #667eea;
}

.request-info {
  flex: 1;
}

.request-info h5 {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.25rem 0;
}

.request-info p {
  color: #718096;
  margin: 0 0 0.25rem 0;
  font-size: 0.9rem;
}

.request-date {
  font-size: 0.8rem;
  color: #9ca3af;
}

.request-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.request-status.pending {
  background: #fef3c7;
  color: #92400e;
}

.request-status.completed {
  background: #d1fae5;
  color: #065f46;
}

.request-status.failed {
  background: #fee2e2;
  color: #991b1b;
}

.no-requests {
  text-align: center;
  padding: 2rem;
  color: #9ca3af;
}

.no-requests i {
  font-size: 2rem;
  margin-bottom: 1rem;
}

/* Bulk Upload */
.bulk-uploader {
  max-width: 800px;
  margin: 0 auto;
}

.bulk-drop-zone {
  border: 2px dashed #cbd5e0;
  border-radius: 15px;
  padding: 3rem;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  background: rgba(102, 126, 234, 0.02);
}

.bulk-drop-zone.active {
  border-color: #667eea;
  border-style: solid;
  background: rgba(102, 126, 234, 0.1);
  transform: scale(1.02);
}

.bulk-drop-zone.has-files {
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.05);
}

.drop-zone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.drop-zone-content i {
  font-size: 4rem;
  color: #cbd5e0;
  margin-bottom: 1rem;
}

.drop-zone-content h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
}

.file-label {
  color: #667eea;
  cursor: pointer;
  text-decoration: underline;
  font-weight: 600;
}

/* Files Preview */
.files-preview h4 {
  font-size: 1.3rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 1.5rem 0;
  text-align: center;
}

.files-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.file-name {
  flex: 1;
  font-weight: 500;
  color: #2d3748;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  font-size: 0.8rem;
  color: #9ca3af;
}

.remove-btn {
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  cursor: pointer;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.more-files {
  grid-column: 1 / -1;
  text-align: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  color: #718096;
  font-weight: 500;
}

.upload-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

/* Results */
.compliance-overview {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
  padding: 2rem;
  background: #f8fafc;
  border-radius: 15px;
}

.compliance-metric {
  display: flex;
  align-items: center;
  justify-content: center;
}

.metric-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: conic-gradient(#10b981 var(--progress), #e2e8f0 var(--progress));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.metric-circle::before {
  content: '';
  position: absolute;
  width: 80px;
  height: 80px;
  background: white;
  border-radius: 50%;
}

.metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  z-index: 1;
}

.metric-label {
  font-size: 0.8rem;
  color: #718096;
  z-index: 1;
}

.compliance-breakdown {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.breakdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 8px;
  font-weight: 600;
}

.breakdown-item.passing {
  background: rgba(16, 185, 129, 0.1);
  color: #065f46;
}

.breakdown-item.failing {
  background: rgba(239, 68, 68, 0.1);
  color: #991b1b;
}

.breakdown-item.pending {
  background: rgba(245, 158, 11, 0.1);
  color: #92400e;
}

/* Results List */
.results-list h4 {
  font-size: 1.2rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 1.5rem 0;
}

.result-item {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.result-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.result-header h5 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.result-score {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.9rem;
}

.result-score.excellent {
  background: #d1fae5;
  color: #065f46;
}

.result-score.good {
  background: #fef3c7;
  color: #92400e;
}

.result-score.warning {
  background: #fed7aa;
  color: #9a3412;
}

.result-score.poor {
  background: #fee2e2;
  color: #991b1b;
}

.result-summary {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.text-success {
  color: #10b981;
}

.text-danger {
  color: #ef4444;
}

.text-warning {
  color: #f59e0b;
}

.result-actions {
  display: flex;
  gap: 0.75rem;
}

/* Document Header Styling */
.result-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.document-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.document-info h5 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.document-type-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  width: fit-content;
}

.doc-type-coi {
  background: #e8f5e8;
  color: #2e7d2e;
  border: 1px solid #c3e6c3;
}

.doc-type-policy {
  background: #e3f2fd;
  color: #1565c0;
  border: 1px solid #bbdefb;
}

.doc-type-endorsement {
  background: #fff3e0;
  color: #ef6c00;
  border: 1px solid #ffcc02;
}

.doc-type-amendment {
  background: #f3e5f5;
  color: #7b1fa2;
  border: 1px solid #e1bee7;
}

.doc-type-unknown {
  background: #f5f5f5;
  color: #757575;
  border: 1px solid #e0e0e0;
}

/* RAG Intelligence Insights */
.rag-insights {
  margin-top: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, #e8f4ff 0%, #f0f8ff 100%);
  border-radius: 8px;
  border-left: 4px solid #007bff;
}

.rag-insights h6 {
  margin: 0 0 0.75rem 0;
  color: #007bff;
  font-weight: 600;
  font-size: 0.9rem;
}

.insights-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.insight-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.5rem;
  background: rgba(255,255,255,0.7);
  border-radius: 6px;
}

.insight-confidence {
  background: #007bff;
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 45px;
  text-align: center;
}

.insight-text {
  flex: 1;
  font-size: 0.85rem;
  color: #495057;
  line-height: 1.4;
}

.more-insights {
  text-align: center;
  color: #6c757d;
  font-size: 0.8rem;
  font-style: italic;
  padding: 0.25rem;
}

/* Pattern Matches */
.pattern-matches {
  margin-top: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, #fff4e6 0%, #fff8f0 100%);
  border-radius: 8px;
  border-left: 4px solid #fd7e14;
}

.pattern-matches h6 {
  margin: 0 0 0.75rem 0;
  color: #fd7e14;
  font-weight: 600;
  font-size: 0.9rem;
}

.patterns-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.pattern-badge {
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.pattern-badge.high-confidence {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.pattern-badge.medium-confidence {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.pattern-badge.low-confidence {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.more-patterns {
  color: #6c757d;
  font-size: 0.8rem;
  font-style: italic;
}

.btn-sm {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
}

/* Button Styles */
.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  font-size: 0.95rem;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-secondary {
  background: white;
  color: #667eea;
  border: 1px solid #667eea;
}

.btn-outline-primary {
  background: white;
  color: #667eea;
  border: 1px solid #667eea;
}

.btn-outline-secondary {
  background: white;
  color: #6b7280;
  border: 1px solid #d1d5db;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  text-decoration: none;
  color: inherit;
}

.btn-primary:hover {
  color: white;
}

.btn-secondary:hover,
.btn-outline-primary:hover {
  background: #667eea;
  color: white;
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

/* Disabled Workspace */
.disabled-workspace {
  text-align: center;
  padding: 4rem;
  background: rgba(243, 244, 246, 0.5);
  border-radius: 20px;
  border: 2px dashed #d1d5db;
}

.disabled-content {
  max-width: 500px;
  margin: 0 auto;
}

.disabled-content i {
  font-size: 4rem;
  color: #9ca3af;
  margin-bottom: 1.5rem;
}

.disabled-content h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 1rem 0;
}

.disabled-content p {
  color: #9ca3af;
  margin: 0 0 2rem 0;
  line-height: 1.6;
  font-size: 1.1rem;
}

/* Export Section */
.export-section {
  text-align: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e2e8f0;
}

/* Responsive */
@media (max-width: 1024px) {
  .request-workspace {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .compliance-overview {
    flex-direction: column;
    text-align: center;
  }
}

@media (max-width: 768px) {
  .rag-analysis-page {
    padding: 1rem;
  }
  
  .page-header {
    flex-direction: column;
    text-align: center;
    padding: 2rem;
  }
  
  .header-content {
    flex-direction: column;
  }
  
  .page-title {
    font-size: 1.8rem;
  }
  
  .workspace-section {
    padding: 1.5rem;
  }
  
  .files-list {
    grid-template-columns: 1fr;
  }
  
  .result-summary {
    flex-wrap: wrap;
  }
}

/* Strategic Recommendations */
.recommendations-section {
  margin-top: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f7fa 100%);
  border-radius: 8px;
  border-left: 4px solid #00acc1;
}

.recommendations-section h6 {
  margin: 0 0 0.75rem 0;
  color: #00695c;
  font-weight: 600;
}

.recommendations-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.recommendation-item {
  padding: 0.75rem;
  background: white;
  border-radius: 6px;
  border-left: 3px solid #e0e0e0;
}

.recommendation-item.priority-high {
  border-left-color: #d32f2f;
  background: #ffebee;
}

.recommendation-item.priority-medium {
  border-left-color: #f57c00;
  background: #fff3e0;
}

.recommendation-item.priority-low {
  border-left-color: #388e3c;
  background: #e8f5e8;
}

.rec-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.rec-title {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.rec-priority {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
}

.priority-high .rec-priority {
  background: #d32f2f;
  color: white;
}

.priority-medium .rec-priority {
  background: #f57c00;
  color: white;
}

.priority-low .rec-priority {
  background: #388e3c;
  color: white;
}

.rec-description {
  margin: 0;
  font-size: 0.85rem;
  color: #546e7a;
  line-height: 1.4;
}

.more-recommendations {
  padding: 0.5rem;
  text-align: center;
  color: #607d8b;
  font-style: italic;
}

/* Enhancement Opportunities */
.enhancements-section {
  margin-top: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, #f3e5f5 0%, #fce4ec 100%);
  border-radius: 8px;
  border-left: 4px solid #8e24aa;
}

.enhancements-section h6 {
  margin: 0 0 0.75rem 0;
  color: #6a1b9a;
  font-weight: 600;
}

.enhancements-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.enhancement-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: white;
  border-radius: 6px;
  border: 1px solid #e1bee7;
}

.enh-title {
  font-weight: 500;
  color: #2c3e50;
  font-size: 0.9rem;
}

.enh-value {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  background: #8e24aa;
  color: white;
  text-transform: capitalize;
}

/* Detailed Analysis Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
}

.detailed-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from { transform: translateY(-20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(90deg);
}

.modal-content {
  padding: 2rem;
  max-height: calc(90vh - 100px);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f7fafc;
}

.modal-content::-webkit-scrollbar {
  width: 6px;
}

.modal-content::-webkit-scrollbar-track {
  background: #f7fafc;
}

.modal-content::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.detail-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.detail-section h4 {
  margin: 0 0 1rem 0;
  color: #2d3748;
  font-size: 1.2rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.detail-item label {
  font-weight: 600;
  color: #4a5568;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.score-badge, .confidence-badge {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  text-align: center;
  width: fit-content;
}

.requirements-summary {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.req-stat {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  min-width: 120px;
}

.req-stat.passed {
  background: #d4f4dd;
  color: #22543d;
  border: 1px solid #9ae6b4;
}

.req-stat.failed {
  background: #fed7d7;
  color: #742a2a;
  border: 1px solid #feb2b2;
}

.req-stat.unclear {
  background: #fef5e7;
  color: #744210;
  border: 1px solid #f6e05e;
}

.requirement-group {
  margin-bottom: 1.5rem;
}

.req-group-title {
  font-size: 1.1rem;
  margin: 0 0 1rem 0;
  padding: 0.75rem;
  border-radius: 8px;
  font-weight: 600;
}

.req-group-title.passed {
  background: #d4f4dd;
  color: #22543d;
}

.req-group-title.failed {
  background: #fed7d7;
  color: #742a2a;
}

.req-group-title.unclear {
  background: #fef5e7;
  color: #744210;
}

.requirement-item {
  padding: 1rem;
  margin-bottom: 0.75rem;
  border-radius: 8px;
  border-left: 4px solid;
}

.requirement-item.passed {
  background: #f0fff4;
  border-left-color: #48bb78;
}

.requirement-item.failed {
  background: #fffaf0;
  border-left-color: #f56565;
}

.requirement-item.unclear {
  background: #fffff0;
  border-left-color: #ed8936;
}

.requirement-item strong {
  color: #2d3748;
  display: block;
  margin-bottom: 0.5rem;
}

.requirement-item p {
  margin: 0.5rem 0;
  color: #4a5568;
  line-height: 1.5;
}

.evidence, .reason, .notes, .rationale {
  font-style: italic;
  color: #667eea !important;
  font-size: 0.9rem;
}

.insight-item, .pattern-item, .recommendation-item, .opportunity-item {
  padding: 1rem;
  margin-bottom: 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.insight-header, .pattern-header, .rec-header, .opp-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.insight-type, .pattern-type, .rec-type {
  background: #667eea;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.insight-confidence, .pattern-confidence {
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-weight: 500;
}

.rec-priority, .opp-impact, .opp-effort {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: capitalize;
}

.insight-recommendations ul, .opp-steps ol {
  margin: 0.5rem 0 0 0;
  padding-left: 1.5rem;
}

.insight-recommendations li, .opp-steps li {
  margin-bottom: 0.25rem;
  color: #4a5568;
}

/* Priority and confidence classes */
.high-confidence { background: #48bb78; color: white; }
.medium-confidence { background: #ed8936; color: white; }
.low-confidence { background: #f56565; color: white; }

.priority-high { background: #f56565; color: white; }
.priority-medium { background: #ed8936; color: white; }
.priority-low { background: #48bb78; color: white; }

/* Responsive modal */
@media (max-width: 768px) {
  .detailed-modal {
    width: 95%;
    max-height: 95vh;
  }
  
  .modal-header {
    padding: 1rem;
  }
  
  .modal-content {
    padding: 1rem;
  }
  
  .detail-grid {
    grid-template-columns: 1fr;
  }
  
  .requirements-summary {
    flex-direction: column;
  }
  
  .insight-header, .pattern-header, .rec-header, .opp-header {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* Split-Pane Layout for RAG Analysis Modal */
.rag-split-pane-modal {
  max-width: 95vw;
  width: 1400px;
  max-height: 90vh;
}

/* Make it even wider on large screens */
@media (min-width: 1600px) {
  .rag-split-pane-modal {
    width: 1600px;
  }
}

.split-pane-container {
  height: 100%;
  overflow: hidden;
}

.split-pane-layout {
  display: flex;
  height: 70vh;
  min-height: 500px;
  gap: 1rem;
}

.document-panel {
  flex: 0 0 45%;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  overflow: hidden;
}

.results-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  overflow: hidden;
}

.panel-header {
  padding: 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  border-radius: 8px 8px 0 0;
}

.panel-header h4 {
  margin: 0 0 0.25rem 0;
  color: #2d3748;
  font-size: 1.1rem;
  font-weight: 600;
}

.panel-subtitle {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.document-viewer {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.results-content {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

.document-actions {
  padding: 0.75rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  gap: 0.5rem;
}

.btn-outline {
  padding: 0.375rem 0.75rem;
  border: 1px solid #6c757d;
  background: transparent;
  color: #6c757d;
  text-decoration: none;
  border-radius: 4px;
  font-size: 0.875rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.2s;
}

.btn-outline:hover {
  background: #6c757d;
  color: white;
  text-decoration: none;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
}

.document-preview {
  flex: 1;
  border-radius: 8px;
  overflow: auto;
  background: white;
  min-height: 400px;
}

.pdf-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-preview {
  flex: 1;
  width: 100%;
  height: 100%;
  border: none;
  min-height: 400px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px 8px 0 0;
}

.pdf-fallback {
  padding: 10px;
  text-align: center;
  background: #f8f9fa;
  border-radius: 0 0 8px 8px;
  border-top: 1px solid #dee2e6;
}

.pdf-fallback p {
  margin: 5px 0;
}

.pdf-fallback .fas {
  color: #dc3545;
  margin-right: 5px;
}

.preview-placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  text-align: center;
  padding: 2rem;
}

.preview-placeholder i {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.small {
  font-size: 0.875rem;
}

.text-muted {
  color: #6c757d !important;
}



/* Responsive Design for Split-Pane */
@media (max-width: 1200px) {
  .rag-split-pane-modal {
    width: 95vw;
  }
  
  .split-pane-layout {
    flex-direction: column;
    height: auto;
    max-height: 80vh;
  }
  
  .document-panel {
    flex: 0 0 300px;
  }
  
  .results-panel {
    flex: 1;
    min-height: 400px;
  }
}

@media (max-width: 768px) {
  .document-actions {
    flex-direction: column;
  }
  
  .panel-header h4 {
    font-size: 1rem;
  }
  
  .split-pane-layout {
    gap: 0.5rem;
  }
}

/* Customer Overlap Analysis Styles */
.customer-analysis-summary {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #007bff;
}

.analysis-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.stat-label {
  font-weight: 600;
  color: #495057;
}

.stat-value {
  font-weight: bold;
  font-size: 1.1em;
}

.document-risk-assessment {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.risk-level-badge {
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 1rem;
}

.risk-high {
  background: #dc3545;
  color: white;
}

.risk-medium {
  background: #fd7e14;
  color: white;
}

.risk-low {
  background: #28a745;
  color: white;
}

.risk-unknown {
  background: #6c757d;
  color: white;
}

.risk-factors ul {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.customer-clusters {
  margin-top: 1.5rem;
}

.customer-cluster-item {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.cluster-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.entity-name {
  font-weight: bold;
  color: #495057;
}

.entity-role {
  background: #007bff;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.875rem;
}

.cross-doc-badge {
  background: #fd7e14;
  color: white;
}

.legacy-badge {
  background: #17a2b8;
  color: white;
}

.cluster-details {
  display: flex;
  gap: 2rem;
  align-items: center;
}

.risk-info, .compliance-rate {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.risk-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: bold;
}

.compliance-excellent {
  color: #28a745;
  font-weight: bold;
}

.compliance-good {
  color: #20c997;
  font-weight: bold;
}

.compliance-fair {
  color: #fd7e14;
  font-weight: bold;
}

.compliance-poor {
  color: #dc3545;
  font-weight: bold;
}

.compliance-critical {
  color: #dc3545;
  font-weight: bold;
  background: #f8d7da;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

/* Risk Prioritization Styles */
.risk-prioritization-content {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #fd7e14;
}

.priority-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.priority-level {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.priority-badge {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: bold;
  text-transform: uppercase;
}

.adjustments-applied {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #28a745;
  font-weight: 500;
}

.risk-recommendation {
  background: white;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.recommendation-text {
  background: #e7f3ff;
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid #007bff;
  font-weight: 500;
  line-height: 1.6;
}

/* 🤖 Intelligent Compliance Agent Styles */
.agent-evaluation-content {
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  border: 2px solid #dee2e6;
  position: relative;
}

.agent-evaluation-content::before {
  content: '🤖';
  position: absolute;
  top: -10px;
  right: 20px;
  background: white;
  padding: 5px 10px;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.agent-recommendation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #dee2e6;
}

.recommendation-action {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.action-label {
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 500;
}

.action-value {
  font-size: 1.25rem;
  font-weight: bold;
  color: #343a40;
}

.urgency-badge {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: bold;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.urgency-critical {
  background: #dc3545;
  color: white;
}

.urgency-high {
  background: #fd7e14;
  color: white;
}

.urgency-medium {
  background: #ffc107;
  color: #212529;
}

.urgency-low {
  background: #28a745;
  color: white;
}

.urgency-unknown {
  background: #6c757d;
  color: white;
}

.agent-reasoning {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border-left: 4px solid #007bff;
}

.agent-reasoning h6 {
  color: #007bff;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.reasoning-text {
  font-size: 1rem;
  line-height: 1.6;
  color: #495057;
}

.confidence-metrics {
  display: flex;
  gap: 2rem;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
}

.confidence-score {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.confidence-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6c757d;
}

.confidence-bar-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.confidence-bar {
  width: 200px;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}

.confidence-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.confidence-very-high {
  background: #28a745;
}

.confidence-high {
  background: #20c997;
}

.confidence-medium {
  background: #ffc107;
}

.confidence-low {
  background: #fd7e14;
}

.confidence-unknown {
  background: #6c757d;
}

.confidence-percentage {
  font-weight: bold;
  color: #495057;
}

.resolution-timeline {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.timeline-label {
  font-size: 0.875rem;
  color: #6c757d;
}

.timeline-value {
  font-weight: bold;
  color: #007bff;
}

.specific-actions {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border-left: 4px solid #28a745;
}

.specific-actions h6 {
  color: #28a745;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.action-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.action-item i {
  color: #28a745;
}

.agent-risk-factors {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border-left: 4px solid #fd7e14;
}

.agent-risk-factors h6 {
  color: #fd7e14;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.risk-factors-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.risk-factor-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #fff3cd;
  border-radius: 6px;
  border: 1px solid #ffeaa7;
}

.agent-metadata {
  display: flex;
  gap: 2rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.metadata-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6c757d;
  font-size: 0.875rem;
}

.metadata-label {
  font-weight: 500;
}

.metadata-value {
  color: #495057;
}

.agent-error {
  border-left: 4px solid #dc3545;
}

.agent-error .alert {
  margin: 0;
}

/* Responsive Design for Agent Section */
@media (max-width: 768px) {
  .agent-recommendation-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .confidence-metrics {
    flex-direction: column;
    gap: 1rem;
  }
  
  .confidence-bar {
    width: 100%;
  }
  
  .agent-metadata {
    flex-direction: column;
    gap: 1rem;
  }
}

/* Comprehensive Analysis Styles */
.comprehensive-analysis-content {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1rem;
}

.analysis-text {
  line-height: 1.6;
  color: #2d3748;
}

.analysis-text h1, .analysis-text h2, .analysis-text h3 {
  color: #2b6cb0;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.analysis-text h1 { font-size: 1.5rem; }
.analysis-text h2 { font-size: 1.3rem; }
.analysis-text h3 { font-size: 1.1rem; }

/* Gap Intelligence Styles */
.risk-assessment {
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.risk-summary {
  display: flex;
  gap: 2rem;
  margin-bottom: 1rem;
}

.risk-level, .risk-score {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
}

.risk-level.risk-critical { background: #fed7d7; color: #c53030; }
.risk-level.risk-high { background: #feebc8; color: #dd6b20; }
.risk-level.risk-medium { background: #fefcbf; color: #d69e2e; }
.risk-level.risk-low { background: #c6f6d5; color: #38a169; }

.risk-score {
  background: #e2e8f0;
  color: #4a5568;
}

.risk-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.risk-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
}

.risk-label {
  font-weight: 500;
  color: #4a5568;
}

.risk-value.critical { color: #c53030; font-weight: 700; }
.risk-value.high { color: #dd6b20; font-weight: 600; }
.risk-value.total { color: #2d3748; font-weight: 500; }

/* Compliance Patterns Styles */
.compliance-patterns {
  margin-top: 1rem;
}

.pattern-item {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 0.5rem;
}

.pattern-header {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.pattern-type, .pattern-severity {
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
}

.pattern-type.pattern-failure { background: #fed7d7; color: #c53030; }
.pattern-type.pattern-success { background: #c6f6d5; color: #38a169; }
.pattern-type.pattern-warning { background: #feebc8; color: #dd6b20; }

.pattern-severity.severity-critical { background: #c53030; color: white; }
.pattern-severity.severity-high { background: #dd6b20; color: white; }
.pattern-severity.severity-medium { background: #d69e2e; color: white; }
.pattern-severity.severity-low { background: #38a169; color: white; }

/* Processing Info Styles */
.processing-info {
  background: #f7fafc;
  border-radius: 6px;
  padding: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e2e8f0;
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 500;
  color: #4a5568;
}

.info-value {
  font-weight: 600;
}

.text-success { color: #38a169; }
.text-warning { color: #d69e2e; }

/* Predictive Insights Styles */
.predictive-insights {
  background: #ebf8ff;
  border: 1px solid #bee3f8;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
}

.insights-list, .recommendations-list {
  margin-top: 0.5rem;
}

.insights-list ul, .recommendations-list ul {
  margin-left: 1rem;
  color: #2d3748;
}

.insights-list li, .recommendations-list li {
  margin-bottom: 0.5rem;
  line-height: 1.5;
}
</style>